// Statement API (spec §2, §11.2): every credit and debit on the account.
import type { StatementEntry } from '@/types/phase2.types';
import { ApiError, fromPaise, get, getBlob, post } from './client';
import { MOCK_STATEMENT, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/statement';

export type StatementFormat = 'PDF' | 'CSV' | string;

export interface StatementFilter {
  startDate?: string;
  endDate?: string;
  transactionType?: string;
  status?: string;
  channel?: string;
  minAmount?: number;
  maxAmount?: number;
  referenceNumber?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC' | string;
}

export interface StatementEmailRequest {
  startDate: string;
  endDate: string;
  format: StatementFormat;
}

export interface StatementEmailResponse {
  [key: string]: unknown;
}

export interface StatementSummary {
  accountNumber: string;
  currency: string;
  startDate: string;
  endDate: string;
  openingBalance: number;
  closingBalance: number;
  totalCredits: number;
  totalDebits: number;
  totalTransactions: number;
  transactions: StatementEntry[];
}

export interface TransactionSearchPage {
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
  number: number;
  size: number;
  content: StatementEntry[];
}

export interface SpendSummaryItem {
  category: string;
  total: number;
  count: number;
  share: number;
}

export interface AccountTransactionSummary {
  accountNumber: string;
  currency: string;
  currentBalance: number;
  transactions: StatementEntry[];
}

const mockTransactionSummary = async (accountNumber: string): Promise<AccountTransactionSummary> => ({
  accountNumber,
  currency: 'INR',
  currentBalance: 0,
  transactions: await mock.listStatement(),
});

const mapTransactionSummary = (
  accountNumber: string,
  data: Record<string, unknown>,
): AccountTransactionSummary => ({
  accountNumber: String(data.accountNumber ?? accountNumber),
  currency: String(data.currency ?? 'INR'),
  currentBalance: Number(data.currentBalance ?? 0),
  transactions: mapRows(data.transactions ?? []),
});

const notAvailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'The statement API is not available.');
};

const statementPath = (accountNumber: string): string =>
  `/accounts/${encodeURIComponent(accountNumber.trim())}`;

const filterParams = (filter: StatementFilter = {}): Record<string, string | number> =>
  Object.fromEntries(Object.entries(filter).filter(([, value]) => value !== undefined && value !== '')) as Record<string, string | number>;

const mapEntry = (raw: Record<string, unknown>): StatementEntry => ({
  id: String(raw.id ?? raw.transactionId ?? raw.referenceNumber ?? ''),
  date: new Date(String(raw.createdAt ?? raw.initiatedAt ?? raw.valueDate ?? Date.now())).getTime(),
  description: String(raw.description ?? raw.narration ?? raw.transactionType ?? ''),
  counterparty: typeof raw.counterparty === 'string'
    ? raw.counterparty
    : typeof raw.destinationAccountNumber === 'string'
      ? raw.destinationAccountNumber
      : typeof raw.sourceAccountNumber === 'string'
        ? raw.sourceAccountNumber
        : undefined,
  amountRupees: ['CREDIT', 'INTEREST_CREDIT', 'REFUND', 'REVERSAL'].includes(String(raw.transactionType ?? '').toUpperCase())
    ? Math.abs(Number(raw.amountPaise !== undefined ? fromPaise(Number(raw.amountPaise)) : raw.amount ?? 0))
    : -Math.abs(Number(raw.amountPaise !== undefined ? fromPaise(Number(raw.amountPaise)) : raw.amount ?? 0)),
  category: String(raw.channel ?? raw.transactionType ?? 'Other'),
  reference: typeof raw.referenceNumber === 'string' ? raw.referenceNumber : undefined,
});

const mapRows = (data: unknown): StatementEntry[] => {
  const rows = Array.isArray(data)
    ? data
    : (data as { content?: unknown[]; items?: unknown[]; transactions?: unknown[] }).content
      ?? (data as { items?: unknown[] }).items
      ?? (data as { transactions?: unknown[] }).transactions
      ?? [];
  return rows
    .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'))
    .map(mapEntry);
};

export const listStatement = async (accountNumber?: string): Promise<StatementEntry[]> => {
  if (MOCK_STATEMENT) return mock.listStatement();
  if (!accountNumber) return [];
  const data = await get<unknown>(`${statementPath(accountNumber)}/statement`, {
    params: { page: 0, size: 100, sortBy: 'createdAt', sortDirection: 'DESC' },
  });
  return mapRows(data);
};

export const getAccountStatement = async (
  accountNumber: string,
  filter: StatementFilter = {},
): Promise<StatementSummary> => {
  if (MOCK_STATEMENT) {
    const transactions = await mock.listStatement();
    return {
      accountNumber,
      currency: 'INR',
      startDate: filter.startDate ?? '',
      endDate: filter.endDate ?? '',
      openingBalance: 0,
      closingBalance: 0,
      totalCredits: transactions.filter((row) => row.amountRupees > 0).reduce((sum, row) => sum + row.amountRupees, 0),
      totalDebits: transactions.filter((row) => row.amountRupees < 0).reduce((sum, row) => sum + Math.abs(row.amountRupees), 0),
      totalTransactions: transactions.length,
      transactions,
    };
  }
  if (!REAL_API_ENABLED) return notAvailable();
  const data = await get<Record<string, unknown>>(`${statementPath(accountNumber)}/statement`, {
    params: filterParams(filter),
  });
  const transactions = mapRows(data.transactions ?? data);
  return {
    accountNumber: String(data.accountNumber ?? accountNumber),
    currency: String(data.currency ?? 'INR'),
    startDate: String(data.startDate ?? filter.startDate ?? ''),
    endDate: String(data.endDate ?? filter.endDate ?? ''),
    openingBalance: Number(data.openingBalance ?? 0),
    closingBalance: Number(data.closingBalance ?? 0),
    totalCredits: Number(data.totalCredits ?? 0),
    totalDebits: Number(data.totalDebits ?? 0),
    totalTransactions: Number(data.totalTransactions ?? transactions.length),
    transactions,
  };
};

export const searchTransactions = async (
  accountNumber: string,
  query: string,
  filter: Omit<StatementFilter, 'referenceNumber'> = {},
): Promise<TransactionSearchPage> => {
  if (MOCK_STATEMENT) {
    const content = (await mock.listStatement()).filter((row) =>
      `${row.description} ${row.counterparty ?? ''} ${row.reference ?? ''}`.toLowerCase().includes(query.trim().toLowerCase()),
    );
    return { totalPages: 1, totalElements: content.length, first: true, last: true, number: 0, size: content.length, content };
  }
  if (!REAL_API_ENABLED) return notAvailable();
  const { startDate, endDate, ...searchFilter } = filter;
  const data = await get<Record<string, unknown>>(`${statementPath(accountNumber)}/transactions/search`, {
    params: {
      q: query,
      ...(startDate ? { from: startDate } : {}),
      ...(endDate ? { to: endDate } : {}),
      ...filterParams(searchFilter),
    },
  });
  const content = mapRows(data.content ?? data);
  return {
    totalPages: Number(data.totalPages ?? 1),
    totalElements: Number(data.totalElements ?? content.length),
    first: Boolean(data.first ?? true),
    last: Boolean(data.last ?? true),
    number: Number(data.number ?? filter.page ?? 0),
    size: Number(data.size ?? filter.size ?? content.length),
    content,
  };
};

export const getSpendSummary = async (
  accountNumber: string,
  from?: string,
  to?: string,
): Promise<SpendSummaryItem[]> => {
  if (MOCK_STATEMENT) {
    const transactions = await mock.listStatement();
    const debits = transactions.filter((row) => row.amountRupees < 0);
    const totals = new Map<string, { total: number; count: number }>();
    debits.forEach((row) => {
      const current = totals.get(row.category) ?? { total: 0, count: 0 };
      totals.set(row.category, { total: current.total + Math.abs(row.amountRupees), count: current.count + 1 });
    });
    const total = [...totals.values()].reduce((sum, item) => sum + item.total, 0);
    return [...totals.entries()].map(([category, item]) => ({
      category,
      total: item.total,
      count: item.count,
      share: total ? item.total / total : 0,
    }));
  }
  if (!REAL_API_ENABLED) return notAvailable();
  const data = await get<unknown>(`${statementPath(accountNumber)}/spend-summary`, {
    params: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
  });
  return (Array.isArray(data) ? data : []).filter(
    (item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'),
  ).map((item) => ({
    category: String(item.category ?? ''),
    total: Number(item.total ?? 0),
    count: Number(item.count ?? 0),
    share: Number(item.share ?? 0),
  }));
};

export const getRecentTransactions = async (accountNumber: string): Promise<AccountTransactionSummary> => {
  if (MOCK_STATEMENT) return mockTransactionSummary(accountNumber);
  if (!REAL_API_ENABLED) return notAvailable();
  const data = await get<Record<string, unknown>>(`${statementPath(accountNumber)}/recent-transactions`);
  return mapTransactionSummary(accountNumber, data);
};

export const getMiniStatement = async (accountNumber: string): Promise<AccountTransactionSummary> => {
  if (MOCK_STATEMENT) return mockTransactionSummary(accountNumber);
  if (!REAL_API_ENABLED) return notAvailable();
  const data = await get<Record<string, unknown>>(`${statementPath(accountNumber)}/mini-statement`);
  return mapTransactionSummary(accountNumber, data);
};

export const requestStatementEmail = async (
  accountNumber: string,
  request: StatementEmailRequest,
): Promise<StatementEmailResponse> => {
  if (MOCK_STATEMENT) return { requested: true };
  if (!REAL_API_ENABLED) return notAvailable();
  return post<StatementEmailResponse>(`${statementPath(accountNumber)}/statement/email`, request);
};

export const downloadStatement = async (
  accountNumber: string,
  format: StatementFormat = 'PDF',
  filter: StatementFilter = {},
): Promise<Blob> => {
  if (MOCK_STATEMENT) throw new ApiError('MOCK_ONLY', 'Statement downloads are handled by the statement mock.');
  if (!REAL_API_ENABLED) return notAvailable();
  return getBlob(`${statementPath(accountNumber)}/statement/download`, {
    params: { format, ...filterParams(filter) },
  });
};
